import { or, and, not, createMachine } from 'xstate';

import { routePath } from 'config/route-config';
import { actionUrl } from 'config/static-content';
import { events } from 'machines/config/events';
import { guards } from 'machines/config/guards';
import { stringToBool } from 'utils/misc';

export const nonDomesticTransferMachineConfig = {
  id: 'nonDomesticTransferMachine',
  context: {
    guards: {}
  },
  initial: 'home',
  states: {
    home: {
      on: {
        NEXT: [
          {
            target: 'fuelType',
            guard: 'hasAnyMeterValidationRequired'
          },
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'mixedMeters',
            guard: 'hasMixedMeters'
          },
          {
            target: 'personaliseQuote'
          }
        ],

        BLOCK_JOURNEY: {
          target: 'businessUpdatesAnnouncement'
        }
      },
      meta: {
        path: routePath.nondomesticTransfer
      }
    },

    businessUpdatesAnnouncement: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmail'
        },
        ALLOW_JOURNEY: {
          target: 'home'
        }
      },

      meta: {
        path: routePath.businessUpdatesAnnouncement
      }
    },

    businessUpdatesSubmitEmail: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmailSuccess'
        }
      },
      meta: {
        path: routePath.businessUpdatesSubmitEmail
      }
    },

    businessUpdatesSubmitEmailSuccess: {
      meta: {
        path: routePath.businessUpdatesSubmitEmailSuccess
      }
    },

    fuelType: {
      on: {
        NEXT: [
          {
            target: 'meterpointValidation',
            guard: 'meterValidationRequired'
          },
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'personaliseQuote'
          }
        ],
        CHANGE_ADDRESS: {
          target: 'home'
        }
      },
      meta: {
        path: routePath.fuelType
      }
    },

    meterpointValidation: {
      on: {
        NEXT: [
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'mixedMeters',
            guard: 'hasMixedMeters'
          },
          {
            target: 'personaliseQuote'
          }
        ],
        GUIDE_CLICK: {
          target: 'meterPointGuide'
        }
      },
      meta: {
        path: routePath.meterpointValidation
      }
    },

    meterPointGuide: {
      meta: {
        path: routePath.meterpointGuide
      }
    },

    selectOptions: {
      on: {
        ELIGIBLE_FUEL: {
          target: 'personaliseQuote'
        },
        EDIT_ADDRESS: {
          target: 'home'
        },
        HOME_MOVE: [
          {
            target: 'externalContactUs',
            description: `or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])`,

            guard: or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])
          },
          {
            target: 'externalMoveIn',
            guard: 'isBGECustomer'
          },
          {
            target: 'externalBgClassicHomeMove'
          }
        ]
      },
      meta: {
        path: routePath.selectOptions
      }
    },

    mixedMeters: {
      on: {
        NEXT: {
          target: 'personaliseQuote'
        }
      },
      meta: {
        path: routePath.mixedMeters
      }
    },

    personaliseQuote: {
      on: {
        NEXT: [
          {
            target: 'evTariffInformation',
            guard: 'showEvTariffInformation'
          },
          {
            target: 'paymentMethod',
            guard: 'isPrePayOnly'
          },
          {
            target: 'consumptionEntry'
          }
        ]
      },
      meta: {
        path: routePath.personaliseQuote
      }
    },

    evTariffInformation: {
      on: {
        NEXT: [
          {
            target: 'consumptionEntry'
          }
        ]
      },
      meta: {
        path: routePath.evTariffInformation
      }
    },

    paymentMethod: {
      on: {
        NEXT: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.prepayInfo
      }
    },

    consumptionEntry: {
      on: {
        NEXT: [
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.consumptionEntry
      }
    },

    personalInformationSummary: {
      on: {
        NEXT: {
          target: 'home'
        },
        CHANGE_ADDRESS: {
          target: 'home'
        },

        CHANGE_CONSUMPTION: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.personalInformationSummary
      }
    },

    quote: {
      on: {
        NEXT: {
          target: 'tariffDetails'
        },
        CONSUMPTION: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.quote
      }
    },

    tariffDetails: {
      on: {
        NEXT: {
          target: 'createAccount'
        }
      },
      meta: {
        path: routePath.tariffDetails
      }
    },

    createAccount: {
      on: {
        NEXT: [
          {
            target: 'directDebitType',
            description:
              "and(['hasMultipleDirectDebitTypes', 'isAnyDirectDebit'])",
            guard: and(['hasMultipleDirectDebitTypes', 'isAnyDirectDebit'])
          },
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'psrEntry'
          }
        ],
        CHANGE_ADDRESS: { target: 'home' }
      },
      meta: {
        path: routePath.createAccount
      }
    },

    directDebitType: {
      on: {
        NEXT: {
          target: 'directDebit'
        }
      },
      meta: {
        path: routePath.directDebitType
      }
    },

    directDebit: {
      on: {
        NEXT: {
          target: 'psrEntry'
        }
      },

      meta: {
        path: routePath.directDebit
      }
    },

    psrEntry: {
      on: {
        NEXT: [
          {
            target: 'userPreferences'
          }
        ]
      },
      meta: {
        path: routePath.psrEntry
      }
    },

    userPreferences: {
      on: {
        NEXT: {
          target: 'orderSummary'
        }
      },
      meta: {
        path: routePath.userPreferences
      }
    },

    orderSummary: {
      on: {
        NEXT: {
          target: 'orderComplete'
        }
      },
      meta: {
        path: routePath.orderSummary
      }
    },

    orderComplete: {
      meta: {
        path: routePath.orderComplete
      }
    },

    authResponse: {
      meta: {
        path: routePath.authResponse
      }
    },

    externalContactUs: {
      meta: { path: actionUrl.contact }
    },
    externalBgClassicHomeMove: {
      meta: { path: actionUrl.bgClassicHomeMoveUrl }
    },

    externalMoveIn: {
      meta: { path: actionUrl.moveIn }
    }
  },
  ...events
};

const { VITE_XSTATE_DEBUG } = process.env;
const debug = stringToBool(VITE_XSTATE_DEBUG);
// for VS extesnion
/* istanbul ignore next -- @preserve */
debug &&
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QDsD2yAiqC2cAuAlgMYAqATgIbKwBmYZAshUQBYHJgB0LOYAxADkAogA0SAbQAMAXUSgADqlgFC6OSAAeiAOwAWAIyddk7QFZJkgJwAOXQCYAzHYA0IAJ6Jdlu5wfWAbP4Ouqa6waYR-gC+Ua5omLywhKSU1HSMzGwc3LyCohL6skggisqqyOpaCHqGxmYWNvZOrh4I-o6clu2m1tb6pvoO-foxcehYuEnE5FS09Eys7Fw8uHwAQgAyAPIAwgDSAPoAUlsAqgBKwgCaUkUKSioEasVVNUYm5la2ji7uiIOGfS6Wz6ST+bQOAbWBzRWIgeITfDTVJzDKLbIAIwArsoOLBYKd5BAKHg4ABBZBoLHIIhgXDIPB5MS3dSlR7PUBVfR2bTaTiSbmWfRdczWSS6fwtRDWSzvELBOymRUWAyjeHjRLJGZpeaZJacbG4uAEokk8mU1DU2n0xlkjbbADqxzOlyENxkrIe5UqOkC-IilgFljCwcsUoQfV8djs3jFlkh-kk0bVCM1yNm6QWWS4hqW+MJxNJsAAyliMdgVEJsBQCAAbJkSD3FNnel7-AKmTrCwbaGO2BySBzhmVy0wKpWfVVw1OTLUozN67I0LFgWskNzyfjCZlN+5lJ4VNsIbm8-mC4X+UXiyV-BCWU+9S9BBw2CGmSwpjWz9M6tHZzjLqu66bg24iFJ6+4cpo-w8nyAoxheV4SuG-jcpw2iWPGZjQsEwSwmMCTfikGa6uiXCAWuG5bvk4h2HcJRegePrHrBZ4ISKkhisht6DHY1j8jyEQmLovJJnYn6EUixG-lm+oUcB1HMg49EtkxR4nnB54cVxN6tD2fLWGOdghJYpjaBe+HqpJUzSaislLiulEgTsAASZICAA4kIBxkhgGDnEIxbFiyzaMVBryhEYoR8b0BjGNY4bRZwdjcvo+jmY4BgONoEmIjZ2p2YuXC4KSZCKOweAAGoULWBCFgeoG7gxkGHpy7aXl2QrZX2YSDuGEIOMlqXpdywQ9rlaa2QuZGcCV9DlQy1W1fV6CgeBoUtcx+gdp1PY9QOQ48XYJicKhImDkCMYRBNREFdN-5zWVqAVUtdUkg124SHREHsq10HHjtQpdb2jQHeGoLBlFg7GJhDiQiM05flJd2kQ9YClQtVU1W95R8B5pwAJIYN5OwbAT+whXuv1bYD3bdaDfW3veuj8tl773oO2XbTdyPzqj+qwKuYBEHgWzyOUsB8EIZMeQTmzeQAYqc0uU811NHoqaWcL0diXgMoTaNY2jhszrNmJh2ic+Z1g8-lfN-gLQsi2LEtSxgBMkD5fkBUFquqeF-z3rKutjqZPTGP4Ya3lznQQjKThjiJl623OJEO9kgu1sLoviweksuVsDDeQwWyVUIfthX9VSa4YOt6-0IlG4lfhnvYZg9P4RumJZM682n9lcJn2cu3nfAF0XBwl2XYEqZXzE19r1gh-rjfG7el78XDIReJCYKKjbiPWanMlFZwQ-O7n6D54Xxel+X30berbUIAvdcRA3htr60wL8dtRvxhYSO6UYQpx-IVGaFYNCQAYOjegktPoV02hrAYtcl71wNk3I6QJtZBE4tGHCDhQFTX5tkeQFA3A2hgXgHgEBGqzyQc-IYHU+heH0E+aEipwZfHQrDa8vRzKmCISjdOXAnZ4DIMQSqYA2BECznQn6rZGFt04EqSQ5gJTdBlF-f4VgfB+DCJeC8ugDA9yRnbfup8xESKIFImRcjEFP3+gqPkqj1GBH3vecGl1kpfBEryIImED4ETysfcB-4iBXyxNgS+yAhAMjIG4eRj9FFONsP4bWYRoqG25JzLxAQVHmBhHoYSe8hH2wHpwCJ1AokxLieIxJCD1pUxSVUZxKjjpuM0Z4niXh+K60TGHcEOsglWRCWA+6+pNxkFgOgGqBNkA0FQGQas5RSzYGrAkpJzS1JKJZuHfwxiQ7Ql5ODFucM2bpTUUqSOugykWJmlMmZyA5kLKWSsg8ayNmJNcu5LyXt-KBWCk1f2VdEDBD2Wkw5G9sraIjPxUUushSKm7r0O5J8HlwNmbWeZizlnvXQJ8igmyfmeUVsrDYBwSBXAAArl2BXPI84KVGQvsNCk5t49AszZk4YMBgYy3MPmM4hIjOCPKxTit5+LkCEuJW5UlBwdhbAEMWU4DBqUkAJkqhxLSwXGOZRKKFPQYXg1QvxIZ0JO5mQCNzQVk1hEVIAI5YlQKSLZasdUIHsIqXwhsghjiTN8cGjhJAqKTD0aMJg1ECuCXa8pp8nUuv4EIBgZICYUoAIqnC2CQOl9DHFVC9Z2bKARIQDj4vYPJIbwRmBijYa8aKwn6gTa6xVyrVXqs1QIbVOynHxllGww2fQrAvnMl4mE6FjIhAiN3XpDaJnZGbfwJWKt6UMP+sYfoRgl6f0CdDXQw4xynWMoZEEcNYxzpIVwPARKCA0BoBgdGNZazwJoqu-NngjJRVCIEWw3dUJBoHKG-eEbLahAvSKukT703OtdQgt9Hr7BCk4IMGEMZy0HP0GOkNhljLtxlIOQ24GKlEDIGAM0ZIiAROpIyODeaEMynSeCNuXRLbpVhYqWUBy+J8UCaGZOtrbpxpmiRsjpIKNUYZHwElfzfIAt9vBntBbP1To0b+wImHbyaxUahYYXRgjM3EgJvu6L-wQAIKRkWD6MQqAUm6kFzEBid2SqZKw74zJAl0mCiw2sLAgwOTpQhRnzEmf1GZizeArM2aonwbtAcECOb6S50yplzLcVaMirdaihjBmuU0Ijp8wvZ0izR19dHFOIFCNtfknFLbmSBgYUw4YByGCCG3dTfhbDBnyzNQrlmwDWcZLF0F8WDD8QsEbEw3Zgz9HDIqdJQwYSOYlLohGMbBP3P-PIWAZA6mbNowo8r8X-OdCVL2K6aUUqzcijKKdqEBr2GhN1zb23dsNJok091h2Z38VMjyaMpkLsadaGzXwkal7brSR+ILoT51cC22QHYFBxZYlI3Zhlz9ExJlDWopew6BSeZftdnL3dgEKke9D8Zl7OA4noNS0j6QwA0jgGjtdVR2inhSiEPBWXgizfaMhtuZl5QYUGE9-USyID0BlW9ncZW4uXmMJ0JwxkzJgm2vuzT-PLpmD0GOEXgXEaS-gMUXuwXG0cAO3FgAtATm3YvsgrDAJb4b74fDGEhHptRd3DqtFMp0SdBztAHK3v0e3OYcR5hNIWc0VImc2md8xLoIakwmHhsEG7w5ZRmRV5bJ8PQhRh4NBHvEUezQljLBWPAVYn0J-Uj0MbDdO5eFc4zVoI5s8hFz93fPq3Rmxo2-qXMJeCxl9LOWSs1Y6ylko8aWvz8UpBBwelPTOSgi-Db1n3snewTd5lL303MOqfySonP-6oJF8SgEVxPx2UUK61OgOAYVgDkiUhIXx6mNXorT+vZ9SQQ+kKg-wSiXIa6+6Gz+jGIBApTbS8rv6wJkDUrPQMgeRYh1RO7JKHYL6DTWoYRBCr7NBMx6A+IRDRiOb+qBZrbGbm6DxiIjxXyn7VzHpGC8igZYSKiODhheCyiBgwjeBB6DjhrRp97rYhbZCQLQLwHG7bJxbRjRgqJsJbwwrsGgEwSboXQWDmBdA-wUHCFUGw6irkKULow0IMGIDsQP5Kic7gqXY8TcIsbRj1DbQQimJHyU4QZZwizWK2LEBZymEIBOBOaOCBCWrcY9BcI2CdDggOHmBOEgIU7CrEaRLRLlCvZ+GLbpIobBh6ADiBg2BeL2DMEGCYRsJ1DpSF5irPLYqvJ4qrJRJfJ+EVq2F6IvjGD2B-omCdyF6LoNFpQsxdC2BGyDDxRjqygvgcYDhqIAguFCr2qnzXoSJ3oPrXp1hSGfZxYhiDTTrviDrvi9AAZVqgaRq6zDBCEH5uEVKQZ1jQaJoNEvibHTr3i9C7EJQ8SDD9oCiGyYRzaoSCLxGzHCakbkaUaWgMgNFxwCTq43a5EvHpbvjaygihzbSKi9iDiF69YRb9ZRabgNGYSyjP4XS4JpRA5mFwlDpmDbSpR4LTH96iFcDonFZ+FKjQinTqbBrBEhB85VohhhBjEGCcrlEvbxKtDSEu5jiDTZT3i4QCjGLEkvyoTME1C2Cfx6AjJnEJGnzw6I7I6kaMkBAuKXgQjGQwioQ+4kmdh7yRyobChB4hCF404IH070CM60irG-4Y6gh9EOHghwxmSGx856KghQlJj2A7yF4S5S51FErClrHDZBAYQP6BDxiQHP7+m+CBmhjBnGTxhhlkCS4I44DyBZykh+GBBhC+ChC9TBg3YE66wBmcQZnGRZk6Fqn-H-hgAaClSVE7DoDXoiynCuno7-QxhY7HQsJ-oxjeAE5mSDSIbvgvhAzNaF7tmdk1RrBQA7C1gUD4jEAuS8AMCoAABu6BIp88gYPgo5QI45XxBOP85Z4I-QuOHwUOlBZu+hy59AlR+5R58yfhw555nEl57i15KERBmsqeTgvIfgMQMQQAA */
      ...nonDomesticTransferMachineConfig
    },
    {
      guards
    }
  );
